import { AbstractControl, ValidationErrors } from '@angular/forms';
import { isEmpty } from 'lodash';
import { IConditionalOption, IField } from '../model/field';

export interface IConditionalFields {
  [key: string]: IConditionalOption;
}

export type ExpressionFn = (modelValue?: {
  [key: string]: any;
  formState?: any;
}) => boolean;

export function createExpressionProperties(
  conditionalFields: IConditionalFields,
  field?: IField
): { [key: string]: ExpressionFn } {
  if (!conditionalFields) {
    return {};
  }
  return Object.keys(conditionalFields).reduce((acc, fieldKey) => {
    return {
      [`templateOptions.${fieldKey}`]: createConditionFunction(
        conditionalFields,
        fieldKey,
        field
      ),
      ...acc,
    };
  }, {});
}

function createConditionFunction(
  conditionalFields: IConditionalFields,
  fieldKey: string,
  field?: IField
): ExpressionFn {
  return (model) => {
    switch (fieldKey) {
      // ideally we would also switch on read-only
      case 'visibility':
        const required = innerConditionalFn(
          conditionalFields,
          'required',
          field
        )(model);
        if (required) {
          return true;
        }
        return innerConditionalFn(conditionalFields, fieldKey, field)(model);
      default:
        return innerConditionalFn(conditionalFields, fieldKey, field)(model);
    }
  };
}

function innerConditionalFn(
  conditionalFields: IConditionalFields,
  fieldKey: string,
  field?: IField
): ExpressionFn {
  if (field && field.required && fieldKey === 'required') {
    return () => true;
  }

  // maybe use a get with a default here
  const conditionalField = conditionalFields[fieldKey];
  if (!conditionalField) {
    return () => false;
  }

  if (conditionalField.value === 'false') {
    return () => false;
  }

  if (conditionalField.value === 'true') {
    return () => true;
  }

  if (conditionalField.conditions && conditionalField.conditions['includes']) {
    return createIncludesFunction(conditionalField.conditions['includes']);
  }

  return () => false;
}

function createIncludesFunction(
  includes: { fieldTip: string; values: any[] }[]
) {
  // returning a function (model: formModel) => boolean

  return (model: { [key: string]: any }): boolean => {
    if (isEmpty(model)) {
      return false;
    }

    // Using !includes as .some searches for falsey values
    // If all values are true (.some returns false) (which is true for us)
    return !includes.some(
      ({ fieldTip, values }) =>
        // .some will return (true) as soon as we find a falsey value
        !valuesPresent(values, model[fieldTip])
    );
  };
}

function valuesPresent(values, modelValues): boolean {
  if (Array.isArray(modelValues)) {
    return checkArrayOfValues(values, modelValues);
  } else {
    return checkStringValue(values, modelValues);
  }
}

function checkArrayOfValues(values, modelValues): boolean {
  return values.some((r) => modelValues.includes(r));
}

function checkStringValue(values, modelValue): boolean {
  return values.includes(modelValue);
}

export function checkIfEmailIsValid(
  control: AbstractControl
): ValidationErrors | null {
  const EMAIL_REGEXP: RegExp =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
  if (!control.value) {
    return null;
  }

  const regExpInstance = new RegExp(EMAIL_REGEXP);

  if (!regExpInstance.test(control.value)) {
    return { email: true };
  }

  return null;
}
